/**
 * Photo Gallery page.
 * Desktop: scattered "polaroid" photos with random rotation and hover effects.
 * Mobile: clean 2-column grid. Both layouts open a lightbox dialog with
 * keyboard-navigable prev/next controls.
 */
import { motion } from "framer-motion";
import { useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import Navigation from "@/components/Navigation";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import Footer from "@/components/Footer";
import GrainOverlay from "@/components/GrainOverlay";
import PageFrame from "@/components/PageFrame";
import VariableHeadline from "@/components/VariableHeadline";
import BlurImage from "@/components/BlurImage";

// Import all hero images
import hero01 from "@/assets/hero/hero-01.jpg?format=webp&quality=85";
import hero02 from "@/assets/hero/hero-02.jpg?format=webp&quality=85";
import hero03 from "@/assets/hero/hero-03.jpg?format=webp&quality=85";
import hero04 from "@/assets/hero/hero-04.jpg?format=webp&quality=85";
import hero05 from "@/assets/hero/hero-05.jpg?format=webp&quality=85";
import hero06 from "@/assets/hero/hero-06.jpg?format=webp&quality=85";
import hero07 from "@/assets/hero/hero-07.jpg?format=webp&quality=85";
import hero08 from "@/assets/hero/hero-08.jpg?format=webp&quality=85";
import hero09 from "@/assets/hero/hero-09.jpg?format=webp&quality=85";
import hero10 from "@/assets/hero/hero-10.jpg?format=webp&quality=85";
import hero11 from "@/assets/hero/hero-11.jpg?format=webp&quality=85";
import hero12 from "@/assets/hero/hero-12.jpg?format=webp&quality=85";
import liveSessionImage from "@/assets/live-session.jpg?w=1920&format=webp";
import backgroundImage from "@/assets/background.jpg?format=webp&quality=85";

const photoData = [
  { id: "1", src: hero01, alt: "Hans-Joachim Bucher playing drums at a Thursday Jam rehearsal" },
  { id: "2", src: hero02, alt: "Thursday Jam performing live on stage in Berlin" },
  { id: "3", src: hero03, alt: "Thursday Jam live performance - blues rock trio on stage" },
  { id: "4", src: hero04, alt: "Thursday Jam on stage under concert lighting" },
  { id: "5", src: hero05, alt: "Thursday Jam band shot - Sebastian, Hans-Joachim, and Robert Bucher" },
  { id: "6", src: hero06, alt: "Sebastian Bucher playing guitar with Thursday Jam" },
  { id: "7", src: hero07, alt: "Thursday Jam concert - blues rock performance in Berlin" },
  { id: "8", src: hero08, alt: "Thursday Jam live set - the Bucher family band on stage" },
  { id: "9", src: hero09, alt: "Thursday Jam performing their blues rock repertoire" },
  { id: "10", src: hero10, alt: "Robert Bucher on bass with Thursday Jam" },
  { id: "11", src: hero11, alt: "Thursday Jam stage lights during a live performance" },
  { id: "12", src: hero12, alt: "Thursday Jam - Berlin-based blues rock family band" },
  { id: "13", src: liveSessionImage, alt: "Thursday Jam live session recording in the rehearsal room" },
  { id: "14", src: backgroundImage, alt: "Thursday Jam atmospheric concert shot" },
];

// Shuffle once per page load (not per re-render)
const photos = [...photoData].sort(() => Math.random() - 0.5);

// Pre-computed random positions and rotations for polaroids
const polaroidStyles = [
  { x: "2%", y: "5%", rotate: -8, delay: 0 },
  { x: "35%", y: "2%", rotate: 5, delay: 0.05 },
  { x: "68%", y: "8%", rotate: -3, delay: 0.1 },
  { x: "8%", y: "28%", rotate: 6, delay: 0.15 },
  { x: "42%", y: "25%", rotate: -5, delay: 0.2 },
  { x: "75%", y: "22%", rotate: 8, delay: 0.25 },
  { x: "5%", y: "48%", rotate: -4, delay: 0.3 },
  { x: "38%", y: "52%", rotate: 7, delay: 0.35 },
  { x: "70%", y: "45%", rotate: -6, delay: 0.4 },
  { x: "12%", y: "72%", rotate: 4, delay: 0.45 },
  { x: "45%", y: "75%", rotate: -7, delay: 0.5 },
  { x: "72%", y: "68%", rotate: 3, delay: 0.55 },
  { x: "25%", y: "90%", rotate: -5, delay: 0.6 },
  { x: "60%", y: "88%", rotate: 6, delay: 0.65 },
];

const Gallery = () => {
  useDocumentMeta({
    title: "Photo Gallery – Thursday Jam",
    description: "Browse photos of Thursday Jam - live performances, rehearsal sessions, and behind-the-scenes moments with Sebastian, Hans-Joachim, and Robert Bucher in Berlin.",
    canonical: "https://thursdayjam.rocks/gallery",
    ogImage: hero02,
  });
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const navigatePhoto = (direction: "prev" | "next") => {
    if (selectedIndex === null) return;
    if (direction === "prev") {
      setSelectedIndex(selectedIndex === 0 ? photos.length - 1 : selectedIndex - 1);
    } else {
      setSelectedIndex(selectedIndex === photos.length - 1 ? 0 : selectedIndex + 1);
    }
  };

  return (
    <main id="main-content" className="relative min-h-screen bg-background">
      <PageFrame />
      <GrainOverlay />
      <Navigation variant="about" />

      {/* Hero section */}
      <section className="relative pt-32 pb-8 px-6 md:px-24 lg:px-32">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="mb-6 h-px w-24 origin-left bg-foreground/30"
          />
          <VariableHeadline 
            as="h1" 
            pattern="center"
            className="text-5xl md:text-7xl lg:text-8xl mb-4"
          >
            Photo Gallery
          </VariableHeadline>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="font-body text-lg text-muted-foreground"
          >
            Behind the music, inside the moment
          </motion.p>
        </div>
      </section>

      {/* Floating Polaroids */}
      <section className="relative px-6 md:px-24 lg:px-32 pb-24">
        {/* Mobile: responsive grid layout */}
        <div className="block md:hidden">
          <div className="grid grid-cols-2 gap-4">
            {photos.map((photo, index) => (
              <motion.div
                key={photo.id}
                role="button"
                tabIndex={0}
                aria-label={`View photo: ${photo.alt}`}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedIndex(index); } }}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: Math.min(index * 0.05, 0.4) }}
                className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground rounded-sm"
                onClick={() => setSelectedIndex(index)}
              >
                <div className="bg-[#f5f5f0] p-2 pb-8 shadow-lg">
                  <BlurImage
                    src={photo.src}
                    alt={photo.alt}
                    loading="lazy"
                    className="w-full aspect-square object-cover"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Desktop: floating polaroid scatter layout */}
        <div className="hidden md:block relative mx-auto" style={{ height: "180vh", maxWidth: "1400px" }}>
          {photos.map((photo, index) => {
            const style = polaroidStyles[index % polaroidStyles.length];
            return (
              <motion.div
                key={photo.id}
                role="button"
                tabIndex={0}
                aria-label={`View photo: ${photo.alt}`}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedIndex(index); } }}
                initial={{ opacity: 0, y: 100, rotate: style.rotate * 2 }}
                animate={{ opacity: 1, y: 0, rotate: style.rotate }}
                whileHover={{ 
                  scale: 1.08, 
                  rotate: 0, 
                  transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] }
                }}
                transition={{ 
                  duration: 0.8, 
                  delay: style.delay,
                  ease: [0.16, 1, 0.3, 1]
                }}
                className="absolute cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm"
                style={{
                  left: style.x,
                  top: style.y,
                  zIndex: hoveredIndex === index ? 50 : 1,
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onClick={() => setSelectedIndex(index)}
              >
                {/* Polaroid frame */}
                <div className="bg-[#f5f5f0] p-3 pb-12 shadow-2xl transition-shadow duration-300 hover:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.5)]">
                  <BlurImage
                    src={photo.src}
                    alt={photo.alt}
                    loading="lazy"
                    className="w-48 md:w-56 lg:w-64 aspect-square object-cover transition-all duration-500"
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      <Footer />

      {/* Lightbox Modal */}
      <Dialog open={selectedIndex !== null} onOpenChange={() => setSelectedIndex(null)}>
        <DialogContent 
          className="max-w-5xl border-none bg-transparent p-0 shadow-none"
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft") navigatePhoto("prev");
            if (e.key === "ArrowRight") navigatePhoto("next");
          }}
        >
          <VisuallyHidden>
            <DialogTitle>
              {selectedIndex !== null ? photos[selectedIndex].alt : "Photo"}
            </DialogTitle>
          </VisuallyHidden>
          {/* Close Button */}
          <button
            onClick={() => setSelectedIndex(null)}
            className="absolute -right-2 -top-2 z-50 flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-110"
          >
            <X size={20} />
          </button>

          {/* Navigation Buttons */}
          {photos.length > 1 && (
            <>
              <button
                onClick={() => navigatePhoto("prev")}
                aria-label="Previous photo"
                className="absolute left-4 top-1/2 z-50 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-background/80 text-foreground transition-all hover:bg-primary hover:text-primary-foreground"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={() => navigatePhoto("next")}
                aria-label="Next photo"
                className="absolute right-4 top-1/2 z-50 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-background/80 text-foreground transition-all hover:bg-primary hover:text-primary-foreground"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          {selectedIndex !== null && (
            <div className="bg-[#f5f5f0] p-4 pb-16 mx-auto">
              <img
                src={photos[selectedIndex].src}
                alt={photos[selectedIndex].alt}
                className="max-h-[75vh] w-auto object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default Gallery;
